import React, { useState, useEffect, useRef } from 'react';
import { StreamEmbed } from './StreamEmbed';
import { StreamChat } from './StreamChat';
import { useData } from '../backend/firebase';
import { getImagePath } from '../utils/imageRegistry';

const MultiStreamPlayer = ({ event }) => {
    const { teams } = useData();
    const [viewMode, setViewMode] = useState('grid'); 
    const [selectedIndex, setSelectedIndex] = useState(0);
    
    const [mutedState, setMutedState] = useState({}); 
    const [volumeState, setVolumeState] = useState({}); 
    const [multiAudioEnabled, setMultiAudioEnabled] = useState(false);
    const [useNativeControls, setUseNativeControls] = useState(false); 
    
    const [hiddenStreams, setHiddenStreams] = useState({});
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [chatState, setChatState] = useState({});

    const [isMobile, setIsMobile] = useState(false);
    const [mobileFullScreenIdx, setMobileFullScreenIdx] = useState(null);

    // Detect if we are on a mobile screen (under 768px width)
    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 768);
        checkMobile(); // Check on mount
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const playerRef = useRef(null); 

    const toggleFullScreen = async () => {
        if (isMobile) {
            setIsFullScreen(prev => {
                const nextState = !prev;
                if (nextState) document.body.classList.add('mobile-theater-active');
                else document.body.classList.remove('mobile-theater-active');
                return nextState;
            });
        } else {
        if (!document.fullscreenElement) {
            try { await playerRef.current.requestFullscreen(); } catch (err) {}
        } else {
            if (document.exitFullscreen) document.exitFullscreen();
        }
        }
    };
    useEffect(() => {
        return () => document.body.classList.remove('mobile-theater-active');
    }, []);

    useEffect(() => {
        const handleChange = () => setIsFullScreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', handleChange);
        return () => document.removeEventListener('fullscreenchange', handleChange);
    }, []);

    // 1. DATA PREP: Keep track of original indexes so audio states don't break when we filter
    let activeLinks = event?.links?.length > 0 
        ? event.links.map(l => (typeof l === 'string' ? {url: l, label: "", teamId: ""} : l)) 
        : (event?.link ? [{url: event.link, label: "", teamId: ""}] : []);

    // 2. FILTER HIDDEN: This physically removes hidden streams from the grid logic!
    const visibleLinks = activeLinks
        .map((link, i) => ({ ...link, originalIdx: i }))
        .filter(link => !hiddenStreams[link.originalIdx]);

    const hiddenCount = activeLinks.length - visibleLinks.length;

    // --- SMART AUDIO LOGIC ---
    const focusAudio = (targetIdx) => {
        if (multiAudioEnabled || activeLinks.length === 0) return;

        setMutedState(currentMutes => {
            const isEverythingMuted = activeLinks.every((_, i) => currentMutes[i] === undefined || currentMutes[i] === true);
            if (isEverythingMuted) return currentMutes; 

            const newState = { ...currentMutes };
            activeLinks.forEach((_, i) => {
                newState[i] = (i === targetIdx) ? false : true;
            });

            setVolumeState(currentVols => {
                const targetFinalVol = (currentVols[targetIdx] || 0) === 0 ? 50 : currentVols[targetIdx];
                return { ...currentVols, [targetIdx]: targetFinalVol };
            });

            return newState;
        });
    };

    useEffect(() => {
        if (viewMode === 'single' || viewMode === 'focus') {
            focusAudio(selectedIndex);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIndex, viewMode]);
    useEffect(() => { if (isMobile && viewMode === 'focus') setViewMode('grid'); }, [isMobile, viewMode]);

    const handleSwitchToView = (mode) => {
        if ((mode === 'single' || mode === 'focus') && !multiAudioEnabled) {
            const unmutedLink = visibleLinks.find(l => mutedState[l.originalIdx] === false);
            if (unmutedLink) setSelectedIndex(unmutedLink.originalIdx);
        }
        setViewMode(mode);
    };

    const handleHideStream = (idx) => {
        setHiddenStreams(prev => ({ ...prev, [idx]: true }));
        // If we hide the stream we are currently focused on, snap to the next available one
        if (idx === selectedIndex) {
            const nextAvail = visibleLinks.find(l => l.originalIdx !== idx);
            if (nextAvail) {
                setSelectedIndex(nextAvail.originalIdx);
                if (viewMode === 'single' || viewMode === 'focus') focusAudio(nextAvail.originalIdx);
            }
        }
    };

    const toggleMute = (idx) => {
        setMutedState(prev => {
            const isCurrentlyMuted = prev[idx] === undefined ? true : prev[idx];
            if (isCurrentlyMuted) {
                if (!multiAudioEnabled) {
                    const nextMutes = {};
                    activeLinks.forEach((_, i) => nextMutes[i] = (i === idx) ? false : true);
                    return nextMutes;
                }
                return { ...prev, [idx]: false };
            } else {
                return { ...prev, [idx]: true };
            }
        });

        setVolumeState(prev => {
            const isCurrentlyMuted = mutedState[idx] === undefined ? true : mutedState[idx];
            if (isCurrentlyMuted && (prev[idx] || 0) === 0) return { ...prev, [idx]: 50 };
            return prev;
        });
    };

    const handleVolumeChange = (idx, val) => {
        const parsedVal = parseInt(val);
        setVolumeState(prev => ({...prev, [idx]: parsedVal}));
        
        if (parsedVal > 0 && mutedState[idx] !== false) {
            if (multiAudioEnabled) {
                setMutedState(prev => ({ ...prev, [idx]: false }));
            } else {
                setMutedState(prev => {
                    const nextMutes = {};
                    activeLinks.forEach((_, i) => nextMutes[i] = (i === idx) ? false : true);
                    return nextMutes;
                });
            }
        } else if (parsedVal === 0) {
            setMutedState(prev => ({ ...prev, [idx]: true }));
        }
    };

    const toggleChat = (idx) => setChatState(prev => ({...prev, [idx]: !prev[idx]}));

    if (!event || activeLinks.length === 0) return null;


    const renderTeamIcon = (teamId) => {
        const team = teams.find(t => t.id.toString() === teamId?.toString());
        if (!team) return null;

        // Use the same logic as the carousel for logo paths
        const logoSrc = team.logoUrl ? getImagePath(team.logoUrl) : 
                    team.logo ? `data:image/svg+xml;utf8,${encodeURIComponent(team.logo)}` : null;

        return (
            <div style={{
                width: 32, height: 32, borderRadius: '50%',
                background: 'rgba(255,255,255,0.05)', // Subtle dark background inside
                padding: '2px', // Spacing between logo and border
                border: `2px solid ${team.color || '#6C5DD3'}`, // Dynamic team color border
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                overflow: 'hidden', 
                flexShrink: 0,
                boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                boxSizing: 'border-box'
            }}>
                {logoSrc ? (
                    <img src={logoSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                ) : (
                    <div style={{ fontSize: 10, color: '#fff', fontWeight: 'bold' }}>
                        {team.name?.charAt(0)}
                    </div>
                )}
            </div>
        );
    };
    // const renderTeamIcon = (teamId) => {
    //     const team = teams.find(t => t.id.toString() === teamId?.toString());
    //     if (!team) return null;
    //     if (team.logo) {
    //         const svgDataUrl = `data:image/svg+xml;utf8,${encodeURIComponent(team.logo)}`;
    //         return <img src={svgDataUrl} alt={team.name} style={{width: 20, height: 20, objectFit: 'contain'}} />;
    //     }
    //     return <div style={{width: 16, height: 16, borderRadius: '50%', background: team.color}}></div>;
    // };

    // --- SMART CSS GRID ENGINE ---
    const getGridItemStyle = (totalVisible, originalIdx) => {
        if (isMobile) {
            // FIX: flexShrink: 0 prevents the browser from crushing the video height
            return { width: '100%', height: 'auto', aspectRatio: '16/9', flexShrink: 0 };
        }

        if (viewMode === 'focus') {
            if (originalIdx === selectedIndex) {
                // Focus: Main stream dominates the top row
                return { gridColumn: '1 / -1', gridRow: '1', width: '100%', height: '100%', minHeight: 0 };
            } else {
                // Focus: Side streams sit neatly in the bottom row
                return { gridRow: '2', width: '100%', height: '100%', minHeight: 0 };
            }
        }

        if (viewMode !== 'grid') {
            return { width: '100%', height: '100%', minHeight: 0 }; // Default for Single Mode
        }

        const relativeIdx = visibleLinks.findIndex(l => l.originalIdx === originalIdx);

        if (totalVisible === 3 && relativeIdx === 0) {
            // FIX: width: 50% shrinks the entire container so the control bar perfectly matches the bottom videos
            return { gridColumn: '1 / -1', justifySelf: 'center', alignSelf: 'center', width: '50%', height: '100%', minHeight: 0 };
        }
        if (totalVisible === 5) {
            if (relativeIdx === 0 || relativeIdx === 1) return { gridColumn: 'span 3', width: '100%', height: '100%', minHeight: 0 };
            return { gridColumn: 'span 2', width: '100%', height: '100%', minHeight: 0 };
        }
        return { width: '100%', height: '100%', minHeight: 0 };
    };

    const getGridContainerStyle = (totalVisible) => {

        if (isMobile) {
            return { gridTemplateColumns: '1fr', gridAutoRows: 'max-content', overflowY: 'auto' };
        }

        if (viewMode === 'focus') {
            // Creates a grid with a massive top row, and splits the bottom row based on stream count
            const bottomCols = totalVisible > 1 ? totalVisible - 1 : 1;
            return { gridTemplateColumns: `repeat(${bottomCols}, 1fr)`, gridTemplateRows: '3fr 1fr' };
        }

        if (viewMode !== 'grid') return {};

        if (totalVisible === 1) return { gridTemplateColumns: '1fr', gridTemplateRows: '1fr' };
        if (totalVisible === 2) return { gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr' };
        if (totalVisible === 3) return { gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr' };
        if (totalVisible === 4) return { gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr' };
        if (totalVisible === 5) return { gridTemplateColumns: 'repeat(6, 1fr)', gridTemplateRows: '1fr 1fr' };

        const cols = Math.ceil(Math.sqrt(totalVisible));
        const rows = Math.ceil(totalVisible / cols);
        return { gridTemplateColumns: `repeat(${cols}, 1fr)`, gridTemplateRows: `repeat(${rows}, 1fr)` };
    };

    const renderCell = (linkObj) => {
        const idx = linkObj.originalIdx;
        
        // Logical states for UI
        const isHidden = viewMode === 'single' && idx !== selectedIndex;
        const isFocusMini = viewMode === 'focus' && idx !== selectedIndex;
        
        // Enforce audio rules: Background streams and Sidebar minis are ALWAYS force-muted
        const isMuted = isHidden || isFocusMini ? true : (mutedState[idx] === undefined ? true : mutedState[idx]);
        const vol = isHidden || isFocusMini ? 0 : (volumeState[idx] || 0);
        const showChat = chatState[idx] || false;
        const isMobileFullScreen = isMobile && mobileFullScreenIdx === idx;

        return (
            <div key={idx} style={{
                ...(isHidden ? {
                    // Keeps Single View background streams alive without dropping Twitch feeds
                    position: 'absolute', width: '100%', height: '100%', opacity: 0, pointerEvents: 'none', zIndex: -100
                } : {
                    display: 'flex', flexDirection: 'column', background: '#3a3a3f', 
                    position: isMobileFullScreen ? 'fixed' : 'relative',
                    ...(isMobileFullScreen ? {
                        top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, width: '100vw', height: '100dvh', borderRadius: 0, border: 'none'
                    } : {
                        borderRadius: 12, border: '2.5px solid #4D4B4F', 
                        // FIX: Removed overflow: 'hidden' so the Chat Box can pop freely out of the cell
                        boxShadow: '0 8px 25px rgba(0,0,0,0.5)',
                        ...getGridItemStyle(visibleLinks.length, idx)
                    })
                })
            }}>
                {/* 1. VIDEO WRAPPER (Added border-radius to preserve the rounded look) */}
                <div style={{ flex: 1, position: 'relative', overflow: 'hidden', display: 'flex', borderRadius: isMobileFullScreen ? 0 : '9px 9px 0 0' }}>
                    <div onDoubleClickCapture={(e) => { e.stopPropagation(); e.preventDefault(); }}style={{ width: '100%', height: '100%', padding: '0px', boxSizing: 'border-box' }}>
                        <StreamEmbed link={linkObj.url} autoplay={true} muted={isMuted} volume={vol} cleanFeed={!useNativeControls} />
                    </div>
                    
                    {/* INVISIBLE OVERLAY: Allows you to click a sidebar stream to bring it into focus */}
                    {isFocusMini && (
                        <div 
                            onClick={() => { setSelectedIndex(idx); }} 
                            style={{position: 'absolute', inset: 0, zIndex: 20, cursor: 'pointer'}}
                            title="Click to Focus"
                        ></div>
                    )}
                </div>
                
                {/* 2. CONTROL BAR */}
                {!isHidden && !isFocusMini && (
                    <div style={{display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#1a1a2e', borderTop: '1px solid #333', flexShrink: 0, flexWrap: 'wrap', borderRadius: isMobileFullScreen ? 0 : '0 0 9px 9px'}}>
                        <div style={{flex: 1, display: 'flex', alignItems: 'center', gap: 6, minWidth: 100}}>
                            {renderTeamIcon(linkObj.teamId)}
                            <span style={{fontSize: 11, fontWeight: 'bold', color: '#ccc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                {linkObj.label || `View ${idx+1}`}
                            </span>
                        </div>
                        <button onClick={() => toggleChat(idx)} style={{ background: showChat ? '#6C5DD3' : 'transparent', border: '1px solid #6C5DD3', color: 'white', borderRadius: 4, padding: '2px 8px', fontSize: 10, cursor: 'pointer', fontWeight: 'bold' }}>
                            💬 Chat
                        </button>
                        <a href={linkObj.url} target="_blank" rel="noopener noreferrer" title="Open Channel" style={{textDecoration: 'none'}}>
                            <button style={{ background: '#FF5C5C', border: 'none', color: 'white', borderRadius: 4, padding: '2px 8px', fontSize: 10, cursor: 'pointer', fontWeight: 'bold' }}>
                                🌐 Visit Stream
                            </button>
                        </a>
                        
                        {/* LOOP PREVENTION TOGGLE */}
                        {!isMobile && (
                        <button 
                            onClick={() => handleHideStream(idx)} 
                            style={{ background: 'transparent', border: '1px solid #555', color: '#8F9EB5', borderRadius: 4, padding: '2px 8px', fontSize: 10, cursor: 'pointer', fontWeight: 'bold' }} 
                            title="Hide this stream entirely from your view"
                        >
                            👁️ Hide
                        </button>
                        )}

                        <div style={{display: 'flex', alignItems: 'center', gap: 8, borderLeft: '1px solid #444', paddingLeft: 8}}>
                            {!isMobile && (
                            <input type="range" min="0" max="100" step="5" value={isMuted ? 0 : vol} onChange={(e) => handleVolumeChange(idx, e.target.value)} style={{width: 50, cursor: 'pointer', accentColor: '#6C5DD3'}} />
                            )}
                            <button onClick={() => toggleMute(idx)} style={{background: 'transparent', border: 'none', color: isMuted ? '#FF5C5C' : 'white', cursor: 'pointer', fontSize: 14, padding: 0}} title={isMuted ? "Unmute" : "Mute"}>
                                {isMuted ? "🔇" : "🔊"}
                            </button>
                        </div>
                    </div>
                )}

                {/* 3. THE CHAT "POP-OUT" (Moved outside the video bounds so it never gets clipped) */}
                {showChat && !isHidden && !isFocusMini && (
                    <div style={{
                        position: 'absolute', 
                        bottom: isMobileFullScreen ? 50 : 45, // Anchors perfectly right above the control bar
                        right: 0, 
                        width: isMobile ? '100%' : '300px', 
                        height: '400px', // HARDCODED TALL HEIGHT tricks Twitch/YouTube into ALWAYS showing the input bar
                        maxHeight: '80vh',
                        background: '#151520', border: '2px solid #6C5DD3',
                        borderRadius: 8, overflow: 'hidden', boxShadow: '0 10px 40px rgba(0,0,0,0.9)', zIndex: 9999, display: 'flex', flexDirection: 'column'
                    }}>
                        <div style={{ padding: '6px 12px', background: '#6C5DD3', fontSize: 11, color: '#fff', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>💬 {linkObj.label || `Stream ${idx+1}`} Chat</span>
                            <button onClick={() => toggleChat(idx)} style={{background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16, lineHeight: 1}}>×</button>
                        </div>
                        <div style={{flex: 1, background: '#111'}}>
                            <StreamChat link={linkObj.url} />
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div 
            ref={playerRef} 
            onDoubleClickCapture={(e) => {
                e.stopPropagation();
                e.preventDefault();
            }}
            style={{
                display: 'flex', flexDirection: 'column', width: '100%',

                // ...(isMobile && isFullScreen ? {
                //     // NEW: 'absolute' covers the top of the page, but lets the body scroll naturally!
                //     position: 'absolute', top: 0, left: 0, right: 0, 
                //     zIndex: 9999, minHeight: '100vh', backgroundColor: 'black'
                // } : {
                height: (isFullScreen && !isMobile) ? '100vh' : 'auto', 
                aspectRatio: (isFullScreen && !isMobile) ? 'auto' : '16/9',
                background: (isFullScreen && !isMobile) ? 'black' : 'transparent',
                position: 'relative'
                // })
            }}
        >
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 15px', background: '#151823', borderBottom: '1px solid #2D2B3F', flexWrap: 'wrap'}}>
                <div style={{fontWeight: 'bold', color: 'white', display:'flex', alignItems:'center', gap: 10, fontSize: 14}}>
                    {event.result && <span style={{background:'#FFD700', color:'black', fontSize:10, padding:'2px 6px', borderRadius:4}}>FINAL</span>}
                    <span style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: isFullScreen ? 400 : 200}}>
                        {event.title}
                    </span>
                </div>
                
                <div style={{display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap'}}>
                    
                    {/* RESTORE BUTTON (Only appears if streams are hidden) */}
                    {hiddenCount > 0 && (
                        <button 
                            onClick={() => setHiddenStreams({})} 
                            style={{ background: '#6C5DD3', border: 'none', color: 'white', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 'bold' }}
                        >
                            👁️ Restore {hiddenCount} Hidden
                        </button>
                    )}

                    {!isMobile && (
                    <button 
                        onClick={() => setMultiAudioEnabled(!multiAudioEnabled)} 
                        style={{ background: multiAudioEnabled ? '#26A65B' : 'transparent', border: '1px solid #444', color: multiAudioEnabled ? 'white' : '#ccc', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 'bold' }}
                    >
                        {multiAudioEnabled ? "🔊 Multi-Audio ON" : "🔈 Multi-Audio OFF"}
                    </button>
                    )}

                    {visibleLinks.length > 1 && (
                        <div style={{display: 'flex', background: '#0C0A1A', borderRadius: 8, padding: 2}}>
                            <button onClick={() => handleSwitchToView('single')} style={{background: viewMode === 'single' ? '#6C5DD3' : 'transparent', color: viewMode === 'single' ? 'white' : '#8F9EB5', border: 'none', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 'bold'}}>Single</button>
                            {!isMobile && ( <button onClick={() => handleSwitchToView('focus')} style={{background: viewMode === 'focus' ? '#6C5DD3' : 'transparent', color: viewMode === 'focus' ? 'white' : '#8F9EB5', border: 'none', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 'bold'}}>Focus</button>)}
                            <button onClick={() => handleSwitchToView('grid')} style={{background: viewMode === 'grid' ? '#6C5DD3' : 'transparent', color: viewMode === 'grid' ? 'white' : '#8F9EB5', border: 'none', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 'bold'}}>Grid</button>
                        </div>
                    )}
                    
                    <button 
                        onClick={() => {
                            if(window.confirm("Toggling native controls will briefly reload the video streams. Continue?")) setUseNativeControls(!useNativeControls);
                        }} 
                        style={{background: 'transparent', border: '1px solid #444', color: useNativeControls ? '#FFD700' : '#ccc', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 11}}
                    >
                        {useNativeControls ? "⚙️ Native UI: ON" : "⚙️ Native UI: OFF"}
                    </button>
                    {/* {!isMobile && ( */}
                    <button onClick={toggleFullScreen} style={{background: 'transparent', border: '1px solid #444', color: '#ccc', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 11}}>
                        {isFullScreen ? "Exit Fullscreen" : "⛶ Fullscreen"}
                    </button>
                    {/* )} */}
                </div>
            </div>

            <div style={{flex: 1, background: 'black', position: 'relative', overflow: isMobile ? 'visible' : 'hidden', display: 'flex', flexDirection: 'column'}}>
                
                {viewMode === 'single' && visibleLinks.length > 1 && (
                    <div style={{padding: 10, background: '#111', display: 'flex', justifyContent: 'center', gap: 10, flexWrap: 'wrap', flexShrink: 0, order: 2}}>
                        {visibleLinks.map((linkObj) => {
                            const idx = linkObj.originalIdx;
                            const isSel = idx === selectedIndex;
                            return (
                                <button 
                                    key={idx} 
                                    onClick={() => setSelectedIndex(idx)} // <-- Stripped out the dumb override!
                                    style={{ 
                                        background: isSel ? '#6C5DD3' : '#333', 
                                        color: 'white', 
                                        border: 'none', 
                                        padding: '5px 12px', 
                                        borderRadius: 20, 
                                        fontSize: 12, 
                                        cursor: 'pointer', 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: 5 
                                    }}
                                >
                                    {renderTeamIcon(linkObj.teamId)} {linkObj.label || `View ${idx+1}`}
                                </button>
                            );
                        })}
                    </div>
                )}

                <div style={{
                    flex: 1,
                    display: viewMode === 'grid' || viewMode === 'focus' ? (isMobile ? 'flex' : 'grid') : 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    ...getGridContainerStyle(visibleLinks.length),
                    gap: viewMode === 'grid' || viewMode === 'focus' ? 6 : 0, 
                    padding: viewMode === 'grid' || viewMode === 'focus' ? 6 : 0,
                    width: '100%', 
                    height: isMobile ? 'auto' : '100%',
                    order: 1
                }}>
                    {visibleLinks.map((linkObj) => renderCell(linkObj))}
                </div>
            </div>
        </div>
    );
};

export default MultiStreamPlayer;